import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import api from "api/axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

function ActiveCell({ value }) {
  return value ? "Yes" : "No";
}

ActiveCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
};

ActiveCell.defaultProps = {
  value: false,
};

function ActionCell({ value, onSetDefault }) {
  return (
    <MDButton size="small" onClick={() => onSetDefault(value)}>
      Set Default
    </MDButton>
  );
}

ActionCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSetDefault: PropTypes.func.isRequired,
};

function TaxRegimes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [current, setCurrent] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const response = await api.get("/tax/regimes?per_page=100");
      const payload = response?.data;
      const list = payload?.data?.data ?? payload?.data ?? payload ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch tax regimes:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrent = async () => {
    try {
      const response = await api.get(`/tax/regimes/current?month=${month}`);
      setCurrent(response?.data?.data ?? null);
    } catch (error) {
      console.error("Failed to fetch current tax regime:", error);
      setCurrent(null);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchCurrent();
  }, [month]);

  const setDefault = async (id) => {
    try {
      const startDate = `${month}-01`;
      await api.post(`/tax/regimes/${id}/default`, { start_date: startDate });
      await fetchCurrent();
      await fetchAll();
    } catch (error) {
      console.error("Failed to set default tax regime:", error);
    }
  };

  const table = useMemo(
    () => ({
      columns: [
        { Header: "Code", accessor: "code" },
        { Header: "Name", accessor: "name" },
        {
          Header: "Active",
          accessor: "is_active",
          Cell: (cell) => <ActiveCell value={cell.value} />,
        },
        {
          Header: "Action",
          accessor: "id",
          Cell: (cell) => <ActionCell value={cell.value} onSetDefault={setDefault} />,
        },
      ],
      rows,
    }),
    [rows, month]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" alignItems="center" justifyContent="space-between">
                <MDBox>
                  <MDTypography variant="h5">Tax Regimes</MDTypography>
                  <MDTypography variant="button" color="text">
                    Manage DB-driven tax regimes and set the default per month.
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" gap={2} alignItems="center">
                  <TextField
                    size="small"
                    type="month"
                    value={month}
                    onChange={(event) => setMonth(event.target.value)}
                  />
                  <MDButton
                    variant="outlined"
                    onClick={fetchAll}
                    startIcon={<Icon>refresh</Icon>}
                    disabled={loading}
                  >
                    Refresh
                  </MDButton>
                </MDBox>
              </MDBox>

              <Divider />

              <MDBox p={3}>
                <MDBox
                  mb={2}
                  p={2}
                  borderRadius="lg"
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(211,47,47,0.12), rgba(211,47,47,0.04))",
                  }}
                >
                  <MDTypography variant="h6">Current Default for {month}</MDTypography>
                  <MDTypography variant="button" color="text">
                    {current
                      ? `${current.code} — ${current.name}`
                      : "No default set for this month yet."}
                  </MDTypography>
                </MDBox>

                <DataTable
                  table={table}
                  isSorted
                  entriesPerPage={false}
                  showTotalEntries={false}
                  canSearch
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default TaxRegimes;
